import React from 'react';
import { Button, Flex, Space, Tooltip, Typography, Popover, Badge } from 'antd';
import { HighlightOutlined, LeftOutlined, RightOutlined, QuestionCircleOutlined, ClearOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

/**
 * Generic beautify control that:
 *  - calls a provided async beautify function
 *  - manages suggestions / pagination / revert
 *  - updates the parent field via onChange
 *
 * It is intentionally generic over the "value" type so it can be used for:
 *  - a single text field (string)
 *  - compound values such as { question, answer } for QA entries
 *
 * Props:
 *  - value: any | current field value
 *  - onChange: (newValue: any) => void
 *  - runBeautify: (value: any) => Promise<{ suggestions: any[]; analysis?: string; coinsAmount?: number }>
 *  - beautifyLabel: string (button text)
 *  - revertLabel: string (button text)
 *  - size: 'small' | 'middle' | 'large'
 *  - disabled?: boolean (when true and there are no suggestions yet, beautify is disabled)
 */
const BeautifyControl = ({
    value,
    onChange,
    runBeautify,
    beautifyLabel,
    revertLabel,
    size = 'small',
    disabled = false,
    // Optional customization for callers like MessageFooter
    beautifyIcon,
    revertIcon,
    buttonStyle,
    showMeta = true, // when false, hides analysis/warnings/pagination chrome
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState(false);
    const [suggestions, setSuggestions] = React.useState([]);
    const [analysis, setAnalysis] = React.useState('');
    const [coinsAmount, setCoinsAmount] = React.useState(null);
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [warnings, setWarnings] = React.useState([]);
    const originalValueRef = React.useRef(null);

    const hasSuggestions = suggestions.length > 0;
    const hasWarnings = warnings.length > 0;

    const appliedBeautifyIcon = beautifyIcon ?? <HighlightOutlined />;
    const appliedRevertIcon = revertIcon ?? <ClearOutlined />;

    const analysisPopoverContent = (
        <div style={{ maxWidth: 260 }}>
            {analysis && (
                <>
                    <Typography.Text
                        type="secondary"
                        style={{ fontSize: 11 }}
                    >
                        {t('agent_analysis')}
                    </Typography.Text>
                    <Typography.Paragraph
                        style={{
                            fontSize: 12,
                            marginTop: 4,
                            marginBottom: typeof coinsAmount === 'number' ? 12 : 0,
                            whiteSpace: 'pre-wrap',
                            maxHeight: '40dvh',
                            overflowY: 'auto',
                        }}
                    >
                        {analysis}
                    </Typography.Paragraph>
                </>
            )}
            {typeof coinsAmount === 'number' && (
                <Typography.Text
                    style={{ fontSize: 12, color: '#52c41a' }}
                >
                    {t('coins_amount')}: {`- ${Math.abs(coinsAmount)}`}
                </Typography.Text>
            )}
        </div>
    );

    const warningsPopoverContent = hasWarnings ? (
        <div style={{ maxWidth: 260 }}>
            <Typography.Text
                type="danger"
                style={{ fontSize: 11 }}
            >
                {t('warnings')}
            </Typography.Text>
            <div style={{ marginTop: 6 }}>
                {warnings.map((w, idx) => {
                    const isObject = w && typeof w === 'object';
                    const reason = isObject ? (w.reason || '') : String(w || '');
                    const rawIds = isObject
                        ? (w.entry_ids || (w.entry_id ? [w.entry_id] : []))
                        : [];
                    const entryIds = Array.isArray(rawIds)
                        ? rawIds.filter(id => id !== null && id !== undefined && id !== '')
                        : [];

                    return (
                        <div key={idx} style={{ marginBottom: 6 }}>
                            {reason && (
                                <Typography.Text style={{ fontSize: 12 }}>
                                    {reason}
                                </Typography.Text>
                            )}
                            {entryIds.map((id, idIdx) => (
                                <Typography.Text
                                    key={idIdx}
                                    type="secondary"
                                    style={{
                                        display: 'block',
                                        fontSize: 11,
                                    }}
                                >
                                    {t('entry_id_label', 'entry id')}: {id}
                                </Typography.Text>
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    ) : null;

    const handleBeautifyClick = async () => {
        if (disabled) {
            return;
        }
        if (
            value === null ||
            value === undefined ||
            (typeof value === 'string' && !value.trim())
        ) {
            return;
        }

        originalValueRef.current = value;
        setLoading(true);
        try {
            const inputForBeautify =
                typeof value === 'string' ? value.trim() : value;
            const result = await runBeautify(inputForBeautify);
            const list = Array.isArray(result?.suggestions) ? result.suggestions : [];
            if (!list.length) {
                // nothing to show, keep original value
                setSuggestions([]);
                setAnalysis('');
                setCoinsAmount(null);
                setWarnings([]);
                setCurrentIndex(0);
                return;
            }
            setSuggestions(list);
            setAnalysis(result?.analysis || '');
            setCoinsAmount(
                typeof result?.coinsAmount === 'number'
                    ? Math.abs(result.coinsAmount)
                    : null
            );
            setWarnings(Array.isArray(result?.warnings) ? result.warnings : []);
            setCurrentIndex(0);
            onChange(list[0]);
        } finally {
            setLoading(false);
        }
    };

    const handleRevertClick = () => {
        if (originalValueRef.current !== null && originalValueRef.current !== undefined) {
            onChange(originalValueRef.current);
        }
        setSuggestions([]);
        setAnalysis('');
        setCoinsAmount(null);
        setWarnings([]);
        setCurrentIndex(0);
    };

    const handlePrev = () => {
        setCurrentIndex(prev => {
            const next = Math.max(prev - 1, 0);
            const text = suggestions[next];
            if (text !== undefined) {
                onChange(text);
            }
            return next;
        });
    };

    const handleNext = () => {
        setCurrentIndex(prev => {
            const next = Math.min(prev + 1, suggestions.length - 1);
            const text = suggestions[next];
            if (text !== undefined) {
                onChange(text);
            }
            return next;
        });
    };

    const isBeautifyDisabled = disabled && !hasSuggestions;

    return (
        <Flex
            align="center"
            justify="flex-end"
            gap={8}
            style={{ marginTop: 0 }}
        >
            {showMeta && hasWarnings && (
                <Popover content={warningsPopoverContent}>
                    <Badge
                        count={warnings.length}
                        size="small"
                        offset={[0, -2]}
                    >
                        <ExclamationCircleOutlined
                            style={{
                                fontSize: 18,
                                color: '#ff4d4f',
                            }}
                        />
                    </Badge>
                </Popover>
            )}
            {showMeta && hasSuggestions && (
                <Popover content={analysisPopoverContent}>
                    <QuestionCircleOutlined
                        style={{
                            fontSize: 18,
                            color: '#1890ff',
                        }}
                    />
                </Popover>
            )}
            <Tooltip
                title={
                    hasSuggestions
                        ? t('beautify_tooltip_revert')
                        : t('beautify_tooltip_apply')
                }
                mouseEnterDelay={0.25}
            >
                <Button
                    type="text"
                    shape="circle"
                    icon={hasSuggestions ? appliedRevertIcon : appliedBeautifyIcon}
                    size={size}
                    onClick={hasSuggestions ? handleRevertClick : handleBeautifyClick}
                    loading={loading}
                    disabled={isBeautifyDisabled}
                    style={buttonStyle}
                />
            </Tooltip>
            {showMeta && hasSuggestions && (
                <Flex align="center" gap={6}>
                    <Button
                        icon={<LeftOutlined />}
                        size="small"
                        type="text"
                        disabled={currentIndex === 0}
                        onClick={handlePrev}
                    />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {`${currentIndex + 1}/${suggestions.length}`}
                    </Typography.Text>
                    <Button
                        icon={<RightOutlined />}
                        size="small"
                        type="text"
                        disabled={currentIndex === suggestions.length - 1}
                        onClick={handleNext}
                    />
                </Flex>
            )}
        </Flex>
    );
};

export default BeautifyControl;


